"use client";

import { useEffect, useState } from "react";
import { RecurringOrder } from "../services/recurring-order-service";
import PickupStaffSelect, { PickupStaff } from "../../../components/custom/pickup-staff-select";
import VehicleLegPicker from "../../../components/custom/vehicle-leg-picker";

interface RecurringAssignStaffModalProps {
  open: boolean;
  onClose: () => void;
  recurring: RecurringOrder | null;
  branchName?: string;
  onSubmit: (
    id: number,
    payload: {
      pickup_driver_ids: number[];
      counter_ids: number[];
      delivery_driver_ids: number[];
      delivery_counter_ids: number[];
      pickup_vehicle_id: number | null;
      delivery_vehicle_id: number | null;
    },
  ) => void;
  loading?: boolean;
}

/** IDs (number[]) -> PickupStaff[] tối thiểu để prefill picker — tên thật được picker tự khớp lại theo id. */
const toStaffValue = (ids?: number[] | null): PickupStaff[] =>
  (ids || []).map((id) => ({ id }));

const RecurringAssignStaffModal: React.FC<RecurringAssignStaffModalProps> = ({
  open,
  onClose,
  recurring,
  branchName,
  onSubmit,
  loading,
}) => {
  const [selectedPickups, setSelectedPickups] = useState<PickupStaff[]>([]);
  const [selectedCounters, setSelectedCounters] = useState<PickupStaff[]>([]);
  const [selectedDeliveries, setSelectedDeliveries] = useState<PickupStaff[]>([]);
  const [selectedDeliveryCounters, setSelectedDeliveryCounters] = useState<PickupStaff[]>([]);
  // Xe tải đi thẳng mặc định theo chiều (null = xe máy)
  const [pickupVehicleId, setPickupVehicleId] = useState<number | null>(null);
  const [deliveryVehicleId, setDeliveryVehicleId] = useState<number | null>(null);

  useEffect(() => {
    if (!open || !recurring) return;
    setSelectedPickups(toStaffValue(recurring.pickup_driver_ids));
    setSelectedCounters(toStaffValue(recurring.counter_ids));
    setSelectedDeliveries(toStaffValue(recurring.delivery_driver_ids));
    setSelectedDeliveryCounters(toStaffValue(recurring.delivery_counter_ids));
    setPickupVehicleId(Number(recurring.pickup_vehicle_id) || null);
    setDeliveryVehicleId(Number(recurring.delivery_vehicle_id) || null);
  }, [open, recurring]);

  if (!open || !recurring) return null;

  const isCounting = Number(recurring.requires_counting) === 1;

  const handleSubmit = () => {
    onSubmit(recurring.id, {
      // Chiều lấy đi xe tải → tài xế = đội hình xe, không lưu danh sách tài xế riêng.
      pickup_driver_ids: pickupVehicleId ? [] : selectedPickups.map((s) => s.id),
      counter_ids: selectedCounters.map((s) => s.id),
      // Chiều giao đi xe tải → người = đội hình xe, không lưu danh sách tài xế giao riêng.
      delivery_driver_ids: deliveryVehicleId ? [] : selectedDeliveries.map((s) => s.id),
      delivery_counter_ids: isCounting ? selectedDeliveryCounters.map((s) => s.id) : [],
      pickup_vehicle_id: pickupVehicleId,
      delivery_vehicle_id: deliveryVehicleId,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg overflow-hidden rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b px-6 py-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Gán nhân sự mặc định</h2>
            <p className="mt-0.5 text-xs text-gray-400">
              Đơn định kỳ #{recurring.id} · {branchName || `CN #${recurring.branch_id}`}
            </p>
          </div>
          <button onClick={onClose} className="text-xl leading-none text-gray-400 hover:text-gray-600">
            &times;
          </button>
        </div>

        {/* Body */}
        <div className="space-y-4 px-6 py-5">
          <p className="rounded-lg bg-blue-50 px-3 py-2 text-xs text-blue-700">
            Danh sách dưới đây sẽ được <span className="font-semibold">gán cứng</span> cho mọi đơn được tự
            động sinh ra từ đơn định kỳ này kể từ bây giờ — không ảnh hưởng các đơn đã tạo trước đó. Muốn đổi
            nhân sự cho một đơn cụ thể vẫn sửa trực tiếp trên đơn đó như bình thường.
          </p>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Tài xế lấy hàng mặc định</label>
            <VehicleLegPicker
              label="Phương tiện"
              value={pickupVehicleId}
              onChange={(id) => {
                setPickupVehicleId(id);
                if (id) setSelectedPickups([]);
              }}
            />
            {!pickupVehicleId && (
              <div className="mt-2">
                <PickupStaffSelect
                  branchId={recurring.branch_id}
                  value={selectedPickups}
                  onChange={setSelectedPickups}
                  placeholder="Chọn tài xế... (để trống = broadcast như cũ)"
                  filterRole="driver"
                />
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium text-slate-500">Giao hàng mặc định</label>
            <VehicleLegPicker
              label="Phương tiện"
              value={deliveryVehicleId}
              onChange={(id) => {
                setDeliveryVehicleId(id);
                if (id) setSelectedDeliveries([]);
              }}
            />
            {!deliveryVehicleId && (
              <div className="mt-2">
                <PickupStaffSelect
                  branchId={recurring.branch_id}
                  value={selectedDeliveries}
                  onChange={setSelectedDeliveries}
                  placeholder="Chọn tài xế giao... (để trống = broadcast như cũ)"
                  filterRole="driver"
                />
              </div>
            )}
          </div>

          {isCounting ? (
            <>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">NV kiểm đếm khi nhận mặc định</label>
                <PickupStaffSelect
                  branchId={recurring.branch_id}
                  value={selectedCounters}
                  onChange={setSelectedCounters}
                  placeholder="Chọn NV kiểm đếm... (để trống = broadcast như cũ)"
                  filterRole="counter"
                />
              </div>
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-500">NV kiểm đếm khi giao mặc định</label>
                <PickupStaffSelect
                  branchId={recurring.branch_id}
                  value={selectedDeliveryCounters}
                  onChange={setSelectedDeliveryCounters}
                  placeholder="Chọn NV kiểm đếm... (để trống = như cũ)"
                  filterRole="counter"
                />
              </div>
            </>
          ) : (
            <p className="text-xs text-gray-400">
              Đơn định kỳ này không yêu cầu kiểm đếm nên không có NV kiểm đếm để gán.
            </p>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 border-t bg-gray-50 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
          >
            Hủy
          </button>
          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60"
          >
            {loading ? "Đang lưu..." : "Lưu"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecurringAssignStaffModal;
